import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ArrowRight, Home as HomeIcon, Building2, Siren, Wrench, CalendarCheck, Wind } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import FAQ from "@/components/FAQ";
import CTABand from "@/components/CTABand";
import { servicesFaqs } from "@/data/faqs";
import SEO from "@/components/SEO";
import { businessSchema, faqSchema, servicesSchema } from "@/data/schema";

const serviceBlocks = [
  {
    id: "emergency",
    icon: Siren,
    accent: "ember",
    title: "24/7 Emergency HVAC Repairs",
    body: "Breakdowns do not check the clock. Our emergency line is answered live around the clock, and a stocked service vehicle dispatches from Trout at any hour — nights, weekends, and holidays included. We handle failed compressors, dead blowers, refrigerant leaks, frozen coils, tripped safety switches, and complete system shutdowns for both homes and commercial facilities. You approve an upfront price before work starts, even at 3 a.m.",
  },
  {
    id: "installation",
    icon: Wrench,
    accent: "frost",
    title: "System Replacement & Installation",
    body: "A new system is only as good as its sizing and installation. Every replacement begins with a proper load calculation, includes ductwork inspection, and finishes with verified airflow and manufacturer-registered warranty coverage. We install high-efficiency central air conditioners, heat pumps, furnaces, ductless mini-splits, and commercial package units — with free written estimates and options at multiple efficiency levels.",
  },
  {
    id: "maintenance",
    icon: CalendarCheck,
    accent: "frost",
    title: "Preventive Seasonal Maintenance",
    body: "Central Louisiana's cooling season is long and brutal on equipment. Our maintenance plans pair a spring cooling tune-up with a fall heating tune-up: coil cleaning, refrigerant verification, electrical testing, drain treatment, filter replacement, and a full safety inspection. Members get priority emergency dispatch, discounted repairs, and documented service history that keeps manufacturer warranties intact.",
  },
  {
    id: "iaq",
    icon: Wind,
    accent: "ember",
    title: "Indoor Air Quality",
    body: "Humidity is Louisiana's invisible HVAC problem. Our indoor air quality services include whole-home dehumidification, high-efficiency media filtration, UV germicidal lights, duct sealing, and ventilation upgrades. If your home feels sticky with the AC running, your family battles allergies, or your commercial space struggles with odors and stale air, an IAQ assessment pinpoints the cause and the fix.",
  },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <SEO
        title="HVAC Repair, Installation & Maintenance | Trout LA"
        description="Residential & commercial HVAC repair, system installation, maintenance plans & 24/7 emergency service across Central Louisiana. Call (318) 312-2112."
        path="/services"
        jsonld={[faqSchema(servicesFaqs), ...servicesSchema(window.location.origin)]}
      />
      {/* PAGE HERO */}
      <section className="relative bg-navy-deep overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1700124113583-81aa99ea2aa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGhlYXQlMjBwdW1wJTIwaG9tZSUyMHN5c3RlbXxlbnwwfHx8fDE3ODg0NTU2NzJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Modern heat pump system installed by McCann and Sons HVAC"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 to-navy-deep" />
          <div className="absolute inset-0 hero-grid-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-5">
            <MaskedLine delay={0.1}>Repair · Maintenance · Installation</MaskedLine>
          </p>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.92] text-white">
            <MaskedLine delay={0.2}>Every System.</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="text-ember">Every Hour.</span> <span className="text-frost">Every Season.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <Marquee items={["Residential HVAC", "Commercial Mechanical", "Emergency Repairs", "Installation", "Maintenance Plans", "Indoor Air Quality", "(318) 312-2112"]} dark />

      {/* RESIDENTIAL */}
      <section id="residential" className="bg-white py-20 lg:py-28 scroll-mt-24" data-testid="residential-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <HomeIcon className="h-6 w-6 text-frost-dark" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost-dark">For Your Home</p>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Residential HVAC Services
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>
              Home comfort in Central Louisiana is not a luxury — it is survival equipment for July and
              August, and a safety system during winter cold snaps. Our residential team repairs, maintains,
              and replaces central air conditioners, gas and electric furnaces, heat pumps, and ductless
              mini-split systems in homes of every age and size, from new construction in Pineville to
              farmhouses outside Jena.
            </p>
            <p>
              Every residential visit follows the same discipline: a complete diagnostic, a clear
              explanation of what failed and why, and an upfront flat-rate price you approve before any
              repair begins. If your system is nearing the end of its life, we will show you the math on
              repair versus replacement honestly — including what a modern high-efficiency system saves on
              a Louisiana electric bill.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-3">
              <a
                data-testid="residential-call-btn"
                href="tel:3183122112"
                className="inline-flex items-center justify-center gap-2 bg-frost hover:bg-frost-dark text-navy-deep font-bold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
              >
                <Phone className="h-4 w-4" />
                Call (318) 312-2112
              </a>
              <Link
                data-testid="residential-quote-btn"
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white font-bold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section id="commercial" className="bg-navy-deep py-20 lg:py-28 relative overflow-hidden grain-overlay scroll-mt-24" data-testid="commercial-section">
        <div className="hero-grid-bg absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <Building2 className="h-6 w-6 text-ember" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">For Your Business</p>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              Commercial Mechanical HVAC
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-300 leading-relaxed">
            <p>
              Commercial HVAC is mechanical work at full scale: rooftop package units, multi-ton split
              systems, exhaust and ventilation equipment, and controls that keep customers comfortable and
              operations compliant. We serve offices, retail spaces, restaurants, churches, and industrial
              facilities across Central Louisiana with the urgency commercial downtime demands.
            </p>
            <p>
              Our commercial maintenance agreements are built around your operating hours — serviced
              quarterly or on a custom schedule, documented for your records, and backed by priority 24/7
              emergency response. When equipment does fail, you get a certified mechanical technician, an
              honest diagnosis, and a flat-rate price before repair begins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-3">
              <a
                data-testid="commercial-call-btn"
                href="tel:3183122112"
                className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-dark text-navy-deep font-bold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
              >
                <Phone className="h-4 w-4" />
                Call (318) 312-2112
              </a>
              <Link
                data-testid="commercial-quote-btn"
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-frost/60 text-frost hover:bg-frost hover:text-navy-deep font-bold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="service-blocks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">The Full Lineup</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Four Ways We Keep Central Louisiana Comfortable
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {serviceBlocks.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.1}>
                <div
                  id={s.id}
                  data-testid={`service-block-${s.id}`}
                  className={`scroll-mt-32 h-full bg-white border-t-4 ${
                    s.accent === "ember" ? "border-t-ember" : "border-t-frost"
                  } border-x border-b border-slate-200 p-8 lg:p-10 rounded-sm`}
                >
                  <s.icon className={`h-8 w-8 mb-5 ${s.accent === "ember" ? "text-ember" : "text-frost"}`} />
                  <h3 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-wide text-navy">{s.title}</h3>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
                    <a
                      data-testid={`service-block-${s.id}-call`}
                      href="tel:3183122112"
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ember hover:text-ember-dark transition-colors"
                    >
                      <Phone className="h-3.5 w-3.5" /> Call Now
                    </a>
                    <Link
                      data-testid={`service-block-${s.id}-quote`}
                      to="/contact"
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-frost-dark hover:text-navy transition-colors"
                    >
                      Request a Quote <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-32" data-testid="services-faq-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Service Questions</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Before You Book, Know This
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Straight answers on installations, commercial service, maintenance plans, and what to watch
              for before a small symptom becomes a full breakdown.
            </p>
          </Reveal>
          <div className="lg:col-span-8">
            <FAQ items={servicesFaqs} />
          </div>
        </div>
      </section>

      <CTABand title="One Call Covers Every System You Own" sub="Residential or commercial, emergency or planned — dispatch starts at (318) 312-2112, 24 hours a day." />
    </div>
  );
}
